import React, {useCallback, useEffect, useRef, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {priority} from '@priority-software/priority-proto-api/lib';
import {Form} from '../../../../../stores/Form';
import {Button, Icon, PriorityIcon} from '@priority-software/priority-style-react';
import {getServerTexts, texts} from '../AdvancedSearchDrawer/texts';
import {SavedSearch} from '../../../types';
import {executeSearch, updateSearches} from '../../../actions/searches';
import {AdvancedSearchDrawer} from '../AdvancedSearchDrawer';
import {SavedSearches} from '../AdvancedSearchDrawer/components/SavedSearches';
import {TriggerButton} from '../AdvancedSearchDrawer/components/SavedSearches/parts/Trigger/TriggerButton';
import styles from '../AdvancedSearchDrawer/components/SavedSearches/parts/Trigger/styles.module.scss';
import {ModernTooltip} from '../../../../../components/Common/Tooltip/ModernTooltip';

interface Props {
    portalSelector?: string;
    currentForm: Form;
    usePortal?: boolean;
    withShortcut?: boolean;
    filterButtonSkin?: 'text' | 'primary' | 'secondary' | 'outline' | 'outline-attention';
    mainSearchButton?: boolean;
    maxResults?: number;
    zIndex?: number;
    tooltipText?: string;
    savedSearchesWrapperCalss?: string;
    useSearchNameAsTooltip?: boolean;
}

export const AdvancedSearches = observer(
    ({
        portalSelector,
        currentForm,
        usePortal,
        withShortcut,
        filterButtonSkin = 'secondary',
        maxResults,
        zIndex,
        tooltipText,
        savedSearchesWrapperCalss,
        useSearchNameAsTooltip
    }: Props) => {
        const {ename, filters, searchStore} = currentForm;
        const {
            isAdvancedSearchOpen,
            toggleAdvancedSearch,
            setFormSavedSearches,
            setDefaultSearch,
            openNewSearch,
            editSearch,
            openSearch,
            savedSearches: searches
        } = searchStore;

        const [isDirty, setIsDirty] = useState(false);
        const [, setTextsLoaded] = useState(false);
        const [isOpen, setIsOpen] = useState(false);
        const isToggleDisabled = useRef(false);

        const handleExecuteSearch = useCallback(
            (search: SavedSearch) => {
                executeSearch(
                    currentForm,
                    {
                        ...search,
                        filterOp: priority.netitems.shared.LogicalOperator.And
                    },
                    maxResults
                );
                setIsOpen(false);
            },
            [currentForm]
        );

        const handleClose = useCallback(async () => {
            isDirty && (await updateSearches(searches, ename));
            setIsOpen(false);
            setIsDirty(false);
        }, [searches, ename, isDirty]);

        const handleOpen = useCallback(() => {
            setIsOpen(prev => !prev);
        }, []);

        const onClose = useCallback(() => setIsOpen(false), []);

        const handleToggleDefault = async (id: string) => {
            if (isToggleDisabled.current) {
                return;
            }

            isToggleDisabled.current = true;

            await updateSearches(setDefaultSearch(id), ename);

            isToggleDisabled.current = false;
        };

        const handleDelete = useCallback(
            async (id: string) => {
                const updatedSearches = searches.filter(item => id !== item.id);

                setFormSavedSearches(updatedSearches);
                await updateSearches(updatedSearches, currentForm.ename);
            },
            [searches]
        );

        const getTooltipText = useCallback(() => {
            const currentSearchId = currentForm?.searchStore.currentSearchId;
            const currentSearch = searches.find(s => s.id === currentSearchId);
            if (useSearchNameAsTooltip && currentSearch) {
                return currentSearch.name;
            } else {
                return tooltipText || texts.filterBtnTooltip;
            }
        }, [currentForm?.searchStore.currentSearchId]);

        const onReorder = (searches: SavedSearch[]) => {
            setFormSavedSearches(searches);
            !isDirty && setIsDirty(true);
        };

        const conditionForClose = isDirty && !searches.length;

        useEffect(() => {
            if (conditionForClose) {
                handleClose();
            }
        }, [conditionForClose, handleClose]);

        useEffect(() => {
            (async () => {
                setTextsLoaded(false);
                await getServerTexts();
                setTextsLoaded(true);
            })();
        }, []);

        const handleEditSearch = useCallback((id: string) => openSearch(id), []);

        const handleOpenDrawer = useCallback(() => {
            openNewSearch();
            setIsOpen(false);
        }, [openNewSearch]);

        const handleCloseDrawer = useCallback(() => {
            toggleAdvancedSearch(false);
        }, []);

        const getSavedSearchesDropdownTitle = () => {
            if (currentForm.filters.length > 0) {
                const search = searches.find(
                    item => item.id === currentForm.searchStore.currentSearchId
                );

                return search?.name || texts.newSearch;
            }
            return texts.filterBtnLabel;
        };

        return (
            <>
                <ModernTooltip tooltip={getTooltipText()} delay={320} position="top-start">
                    {searches?.length || currentForm?.filters?.length > 0 ? (
                        <div className={savedSearchesWrapperCalss}>
                            <SavedSearches
                                portalSelector={portalSelector}
                                usePortal={usePortal}
                                withShortcut={withShortcut}
                                contentClasses={styles.savedSearchContainer}
                                isOpen={isOpen}
                                onOpen={handleOpen}
                                onClose={onClose}
                                onReorder={onReorder}
                                searches={searches}
                                openNewSearch={handleOpenDrawer}
                                executeSearch={handleExecuteSearch}
                                deleteSearch={handleDelete}
                                currentSearchId={currentForm?.searchStore.currentSearchId}
                                openCurrentSearch={currentForm?.searchStore.openSearch}
                                toggleDefault={handleToggleDefault}
                                onEditSearch={handleEditSearch}
                                filterCount={filters.length}
                                trigger={
                                    <TriggerButton
                                        filtersCount={filters.length}
                                        text={getSavedSearchesDropdownTitle()}
                                    />
                                }
                            />
                        </div>
                    ) : (
                        <Button
                            componentClasses={styles.searchButton}
                            onClick={handleOpenDrawer}
                            size="small"
                            skin={filterButtonSkin}
                            prefixIcon={
                                <Icon
                                    iconColor="dark-purple"
                                    size="small"
                                    icon={PriorityIcon.filter}
                                    componentClasses={styles.icon}
                                    counterValue={filters.length}
                                />
                            }
                        >
                            {texts.filterBtnLabel}
                        </Button>
                    )}
                </ModernTooltip>

                {isAdvancedSearchOpen && (
                    <AdvancedSearchDrawer
                        isOpen
                        editSearch={editSearch}
                        form={currentForm}
                        handleToggleDefault={handleToggleDefault}
                        handleDeleteSearch={handleDelete}
                        onClose={handleCloseDrawer}
                        onReorder={onReorder}
                        zIndex={zIndex}
                    />
                )}
            </>
        );
    }
);
